import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  AdminUsuariosService,
  AdminUsuario,
  AdminUsuariosFiltros
} from 'src/app/services/admin-usuarios/admin-usuarios.service';

@Component({
  selector: 'app-admin-usuarios',
  templateUrl: './admin-usuarios.component.html'
})
export class AdminUsuariosComponent implements OnInit, OnDestroy {

  usuarios: AdminUsuario[] = [];
  total = 0;
  page  = 0;
  size  = 25;
  cargando = false;
  soloDuplicados = false;

  filtros: AdminUsuariosFiltros = {
    tipo: 'ALL',
    buscar: '',
    authProvider: '',
    activo: '',
    sortBy: 'createdAt',
    sortDir: 'desc'
  };

  confirmarEliminar: AdminUsuario | null = null;
  mensajeDelete: string = '';
  errorDelete: string = '';
  infoDelete: any = null;
  forceDelete = false;
  procesando = new Set<string>();
  resetandoUsos: Set<number> = new Set();
  exportando = false;

  // ── Envío de correos ────────────────────────────────────────────────────────
  selectedIds: Set<number> = new Set();
  showEmailModal = false;
  emailSubject = '';
  emailContent = '';
  enviandoCorreo = false;
  resultadoEnvio: { enviados: number; fallidos: number; errores: string[] } | null = null;

  private buscarSubject = new Subject<void>();
  private destroy$      = new Subject<void>();

  constructor(
    private svc: AdminUsuariosService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.buscarSubject.pipe(debounceTime(350), takeUntil(this.destroy$))
      .subscribe(() => { this.page = 0; this.cargar(); });
    this.cargar();
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  cargar(): void {
    this.cargando = true;
    this.svc.listar(this.filtros, this.page, this.size).subscribe({
      next: res => { this.usuarios = res.content; this.total = res.total; this.cargando = false; },
      error: () => this.cargando = false
    });
  }

  onTexto(): void { this.buscarSubject.next(); }
  onFiltro(): void { this.page = 0; this.cargar(); }

  get totalPages(): number { return Math.ceil(this.total / this.size); }
  get usuariosFiltrados(): AdminUsuario[] {
    return this.soloDuplicados ? this.usuarios.filter(u => u.duplicado) : this.usuarios;
  }
  get hasDuplicados(): boolean { return this.usuarios.some(u => u.duplicado); }

  prevPage(): void { if (this.page > 0) { this.page--; this.cargar(); } }
  nextPage(): void { if (this.page < this.totalPages - 1) { this.page++; this.cargar(); } }

  sort(campo: string): void {
    if (this.filtros.sortBy === campo) {
      this.filtros.sortDir = this.filtros.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.filtros.sortBy  = campo;
      this.filtros.sortDir = 'asc';
    }
    this.page = 0;
    this.cargar();
  }

  sortIcon(campo: string): string {
    if (this.filtros.sortBy !== campo) return '↕';
    return this.filtros.sortDir === 'asc' ? '↑' : '↓';
  }

  toggle(u: AdminUsuario): void {
    const key = `${u.tipo}-${u.id}`;
    this.procesando.add(key);
    this.svc.toggleActivo(u.tipo, u.id, !u.activo).subscribe({
      next: () => { u.activo = !u.activo; this.procesando.delete(key); },
      error: () => this.procesando.delete(key)
    });
  }

  pedirEliminar(u: AdminUsuario): void {
    this.confirmarEliminar = u;
    this.errorDelete = '';
    this.mensajeDelete = '';
    this.infoDelete = null;
    this.forceDelete = false;
  }
  cancelarEliminar(): void {
    this.confirmarEliminar = null;
    this.errorDelete = '';
    this.infoDelete = null;
  }

  confirmarBorrar(): void {
    const u = this.confirmarEliminar;
    if (!u) return;
    const key = `del-${u.tipo}-${u.id}`;
    this.procesando.add(key);
    this.errorDelete = '';

    const url = `${this.svc['base']}/${u.tipo === 'CANDIDATO' ? 'candidato' : 'empresa'}/${u.id}`
              + (this.forceDelete ? '?force=true' : '');

    this.svc.eliminar(u.tipo, u.id, this.forceDelete).subscribe({
      next: (res: any) => {
        this.procesando.delete(key);
        if (res?.bloqueado) {
          // El backend indica que hay registros relacionados
          this.infoDelete = res;
          this.errorDelete = res.mensaje;
          this.forceDelete = true;
        } else {
          // Éxito
          this.confirmarEliminar = null;
          this.infoDelete = null;
          this.forceDelete = false;
          this.mensajeDelete = 'Usuario eliminado correctamente.';
          this.cargar();
          setTimeout(() => this.mensajeDelete = '', 4000);
        }
      },
      error: (e) => {
        this.procesando.delete(key);
        this.errorDelete = e?.error?.message || e?.error?.mensaje || 'Error al eliminar el usuario. Verifica que no tenga registros relacionados.';
      }
    });
  }

  navigate(path: string): void { this.router.navigate([path]); }

  resetUsos(u: AdminUsuario): void {
    if (u.tipo !== 'CANDIDATO') return;
    this.resetandoUsos.add(u.id);
    this.svc.resetUsos(u.id).subscribe({
      next: () => {
        u.guruMatchUsos = 0;
        u.cvOptimizerUsos = 0;
        this.resetandoUsos.delete(u.id);
      },
      error: () => this.resetandoUsos.delete(u.id)
    });
  }

  cerrarSesion(): void {
    this.authService.cerrarSesion();
    this.router.navigate(['/acceder']);
  }

  exportarExcel(): void {
    if (this.exportando) return;
    this.exportando = true;
    this.svc.exportarExcel().subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        a.href = url;
        a.download = `tg-usuarios-${fecha}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        this.exportando = false;
      },
      error: () => { this.exportando = false; }
    });
  }

  // ── Selección y envío de correos ────────────────────────────────────────────

  /** Candidatos visibles que aplican para envío de correo (tipo CANDIDATO, tienen correo) */
  get candidatosEnviables(): AdminUsuario[] {
    return this.usuariosFiltrados.filter(u => u.tipo === 'CANDIDATO' && u.correo);
  }

  get selectedCount(): number { return this.selectedIds.size; }

  toggleSelect(u: AdminUsuario): void {
    if (u.tipo !== 'CANDIDATO' || !u.correo) return;
    if (this.selectedIds.has(u.id)) {
      this.selectedIds.delete(u.id);
    } else {
      this.selectedIds.add(u.id);
    }
  }

  isSelected(u: AdminUsuario): boolean {
    return this.selectedIds.has(u.id);
  }

  selectAll(): void {
    this.candidatosEnviables.forEach(u => this.selectedIds.add(u.id));
  }

  deselectAll(): void {
    this.selectedIds.clear();
  }

  get selectedCandidatos(): AdminUsuario[] {
    return this.usuarios.filter(u => this.selectedIds.has(u.id));
  }

  abrirModalCorreo(): void {
    if (this.selectedCount === 0) return;
    this.showEmailModal = true;
    this.resultadoEnvio = null;
    // Plantilla HTML por defecto
    if (!this.emailContent) {
      this.emailContent = `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;color:#333;">
  <div style="text-align:center;margin-bottom:24px;">
    <img src="https://tg-empleos.tgconsulting.online/assets/img/icono.png" alt="TG Empleos" style="width:60px;">
  </div>
  <h2 style="color:#1d4ed8;">¡Hola!</h2>
  <p>Te escribimos del equipo de <strong>TG Empleos</strong>.</p>
  <p>Escribe aquí tu mensaje...</p>
  <br>
  <p>Saludos,<br>Equipo TG Empleos</p>
  <hr style="border:none;border-top:1px solid #e2e8f0;margin:24px 0;">
  <p style="font-size:11px;color:#94a3b8;text-align:center;">
    © ${new Date().getFullYear()} TG Empleos. Todos los derechos reservados.
  </p>
</div>`;
    }
  }

  cerrarModalCorreo(): void {
    this.showEmailModal = false;
    this.resultadoEnvio = null;
  }

  enviarCorreo(): void {
    if (this.selectedCount === 0 || this.enviandoCorreo) return;
    if (!this.emailSubject.trim()) return;

    this.enviandoCorreo = true;
    this.resultadoEnvio = null;

    const ids = Array.from(this.selectedIds);
    this.svc.enviarCorreo(ids, this.emailSubject.trim(), this.emailContent).subscribe({
      next: (res) => {
        this.resultadoEnvio = res;
        this.enviandoCorreo = false;
        // Limpiar selección si todos se enviaron exitosamente
        if (res.fallidos === 0) {
          this.selectedIds.clear();
        }
      },
      error: (err) => {
        this.resultadoEnvio = {
          enviados: 0,
          fallidos: ids.length,
          errores: [err?.error?.message || err?.error?.mensaje || 'Error de conexión con el servidor']
        };
        this.enviandoCorreo = false;
      }
    });
  }

  /** Reinicia el modal para un nuevo envío */
  nuevoEnvio(): void {
    this.resultadoEnvio = null;
    this.emailSubject = '';
    this.emailContent = '';
  }

  providerLabel(p: string): string {
    if (!p || p === 'local') return 'Email';
    return p.charAt(0).toUpperCase() + p.slice(1);
  }

  providerColor(p: string): string {
    if (p === 'google')   return '#ea4335';
    if (p === 'linkedin') return '#0a66c2';
    return '#64748b';
  }
}
